import useMediaSelectionActions from '@/actions/useMediaSelectionActions'
import { AppContext, AppContextType } from '@/AppContext'
import CameraIcon from '@/CustomIcons/CameraIcon'
import GearIcon from '@/CustomIcons/GearIcon'
import { queryCurrentMediaSelectionWithFallback } from '@/Logic'
import { AnalyticsEvents } from '@/services/AnalyticsService'
import useAnalytics from '@/services/useAnalytics'
import useRoomActions from '@/services/useRoomActions'
import { BackgroundBlur } from '@/tools/track-processors'
import { Button } from '@/UIComponents2/Button'
import { Checkbox } from '@/UIComponents2/Checkbox'
import MenuButton, { MenuItem } from '@/UIComponents2/MenuButton/MenuButton'
import { RadioGroup } from '@/UIComponents2/RadioGroup'
import { UserContext, UserContextType } from '@/UserContext'
import { LocalVideoTrack } from 'livekit-client'
import { uniqueId } from 'lodash'
import { useContext, useMemo, useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import { sessionSettingsTrigger } from '../../SessionPage'
import CameraOffIcon from '@/CustomIcons/CameraOffIcon'
import { MenuButtonContentTitle } from '@/UIComponents2/MenuButton/MenuButtonContentTitle'

type UserVideoMenuButtonProps = {
    labelPosition?: 'inside' | 'outside'
    triggerClassName?: string
    noMenu?: boolean
}
const UserVideoMenuButton = (props: UserVideoMenuButtonProps) => {
    const { state: appState, dispatch: appDispatch } = useContext(AppContext) as AppContextType
    const { state: userState, dispatch: userDispatch } = useContext(UserContext) as UserContextType

    const { t } = useTranslation()

    const { isMyVideoDeviceMuted, toggleMyVideoDeviceMute } = useRoomActions()

    const { setMediaSelection: changeMediaSelection } = useMediaSelectionActions()

    const { logUserActivityWithContext } = useAnalytics()

    const localTrack = useMemo(() => {
        const localStream = userState.mediaStreams?.video
        if (localStream instanceof LocalVideoTrack) {
            return localStream
        }
        return null
    }, [userState.mediaStreams])

    const currentCameraSelection = queryCurrentMediaSelectionWithFallback(
        userState.mediaSelection,
        userState.mediaSources,
        'video'
    )

    const currentProcessor = useMemo(() => {
        return userState.mediaStreams.videoProcessor
    }, [userState.mediaStreams.videoProcessor])

    const virtualBackgroundEnabled = !!appState.appSettings.canaryFeatures['virtual-backgrounds']

    const showChooseVirtualBackgroundModal = useCallback(() => {
        logUserActivityWithContext(AnalyticsEvents.VIRTUAL_BACKGROUND_MODAL_OPENED, {
            componentSource: 'VideoGalleryBottomToolbar',
        })
        appDispatch({
            type: 'update_app_state',
            value: { showChooseVirtualBackgroundModal: true },
        })
    }, [appDispatch])

    const handleVirtualBackgroundChoice = async (choice: string | null) => {
        if (choice === 'choose_virtual_background') {
            showChooseVirtualBackgroundModal()
        } else if (choice === 'blur') {
            if (!currentProcessor) {
                const blur = await BackgroundBlur(100, appState.appSettings.videoOptions?.optionFramerate)
                await localTrack?.setProcessor(blur)
                userDispatch({
                    type: 'update_media_streams',
                    value: {
                        videoProcessor: {
                            name: 'blur',
                            processor: blur,
                            id: uniqueId(),
                        },
                    },
                })
            } else {
                await currentProcessor.processor.updateTransformerOptions({
                    blurFilterRadius: 100,
                })
                userDispatch({
                    type: 'update_media_streams',
                    value: {
                        videoProcessor: {
                            name: 'blur',
                            processor: currentProcessor.processor,
                            id: uniqueId(),
                        },
                    },
                })
            }
        } else {
            // Set virtual background to none
            await localTrack?.stopProcessor()
            userDispatch({
                type: 'update_media_streams',
                value: { videoProcessor: null },
            })
        }
    }

    const currentVirtualBackgroundChoice = useMemo(() => {
        if (userState.mediaStreams.videoProcessor === null) {
            return 'none'
        }

        if (!!userState.mediaStreams.videoProcessor && userState.mediaStreams.videoProcessor.name !== 'blur') {
            return 'choose_virtual_background'
        }
        return 'blur'
    }, [userState.mediaStreams.videoProcessor, userState.mediaStreams.videoProcessor?.name])

    const menuContent = !props.noMenu && (
        <div className="flex flex-col gap-[18px]">
            <MenuButtonContentTitle text={t('System Video')} icon={CameraIcon} />
            <div className="flex flex-col gap-[12px]">
                <div className="flex flex-col gap-[12px] w-full">
                    <div className="opacity-65">{t('Select Camera')}</div>
                    {userState.mediaSources.video.map((video) => {
                        const isChecked = currentCameraSelection === video.deviceId

                        return (
                            <Checkbox
                                key={video.deviceId}
                                checked={isChecked}
                                label={video.label}
                                onChange={() => {
                                    if (isChecked) {
                                        changeMediaSelection({ video: '_none' })
                                    } else {
                                        changeMediaSelection({ video: video.deviceId })
                                    }
                                }}
                            />
                        )
                    })}
                </div>
                {virtualBackgroundEnabled && (
                    <RadioGroup
                        groupLabel={t('Select Background')}
                        className="gap-[12px]"
                        labelClassName="opacity-65"
                        items={[
                            {
                                label: t('None'),
                                value: 'none',
                            },
                            {
                                label: t('Blur'),
                                value: 'blur',
                            },
                            {
                                label: t('Choose virtual background'),
                                value: 'choose_virtual_background',
                                menuItem: true,
                                onClick: () => {
                                    showChooseVirtualBackgroundModal()
                                },
                            },
                        ]}
                        value={currentVirtualBackgroundChoice}
                        onChange={(value) => {
                            handleVirtualBackgroundChoice(value)
                        }}
                    />
                )}
            </div>
            <MenuItem asChild>
                <Button
                    variant="rectangle"
                    className="text-muse-yellow justify-center"
                    onClick={() => {
                        sessionSettingsTrigger.trigger({ tab: 'video' })
                    }}
                >
                    <GearIcon /> {t('Video Settings')}
                </Button>
            </MenuItem>
        </div>
    )

    return (
        <MenuButton
            label={t('Video')}
            onIconClick={() => {
                toggleMyVideoDeviceMute()
                logUserActivityWithContext(AnalyticsEvents.CLICKED_CAMERA_TOGGLE_BUTTON, {
                    toggleState: !isMyVideoDeviceMuted(),
                    componentSource: 'VideoGalleryBottomToolbar',
                })
            }}
            labelPosition={props.labelPosition}
            triggerClassName={props.triggerClassName}
            icon={
                isMyVideoDeviceMuted() ? (
                    <CameraOffIcon className="text-muse-danger" style={{ fontSize: '1.3rem' }} />
                ) : (
                    <CameraIcon style={{ fontSize: '1.3rem' }} />
                )
            }
            content={menuContent}
        />
    )
}

export default UserVideoMenuButton
